import PropTypes from 'prop-types';
import React from 'react';
import { classNames } from '../../../utils/classnames';

/**
 * @description Render memoized image file media block component
 * @param {Object} props
 * @returns {JSX.Element} Memoized image file media block component
 */
export const ImageFileMedia = (props) => {
  const { alt, src = '#', width, height, className, srcSet, style } = props;
  if (!src) return null;

  return (
    <span
      className="picture-wrapper ec-lazy-img ec-lazy-img--loading ec-lazy-img--loaded"
      style={srcSet?.length > 0 ? style : {}}
    >
      <picture alt={alt} className="ec-lazy-img ec-lazy-img--loaded" style={style}>
        {srcSet?.map((item, index) => (
          <source
            key={item?.media || index}
            media={item?.media}
            srcSet={
              item?.srcSet && !!item?.srcSet?.length
                ? `${src}?${item?.srcSet[0]}&quality=75`
                : `${src}?format=jpg&quality=75&width=${item?.width}&height=${item?.height}&mode=${item.mode}`
            }
          />
        )) || null}

        <img src={src} alt={alt} width={width} height={height} className={className} loading="lazy" />
      </picture>
    </span>
  );
};

ImageFileMedia.propTypes = {
  alt: PropTypes.string,
  className: PropTypes.any,
  height: PropTypes.any,
  src: PropTypes.string,
  srcSet: PropTypes.array,
  style: PropTypes.any,
  width: PropTypes.any
};

/**
 * @description Render memoized in river image file media block component
 * @param {Object} props
 * @returns {JSX.Element} Memoized in river image file media block component
 */
export const InRiverImageFileMedia = (props) => {
  const { alt, src = '#', className, srcSet, style } = props;

  if (!src) return null;

  return (
    <span className="picture-wrapper ec-lazy-img ec-lazy-img--loading ec-lazy-img--loaded">
      <picture alt={alt} className="ec-lazy-img ec-lazy-img--loaded" style={style}>
        {srcSet?.map((item, index) => {
          const itemSrcSet = (item.srcSet || []).map((s) => src + '?' + s).join(', ');
          return <source key={index} media={item.media} srcSet={itemSrcSet} />;
        })}

        <img src={src} alt={alt} className={className} loading="lazy" />
      </picture>
    </span>
  );
};

InRiverImageFileMedia.propTypes = {
  alt: PropTypes.string,
  className: PropTypes.any,
  height: PropTypes.any,
  src: PropTypes.string,
  srcSet: PropTypes.array,
  style: PropTypes.any,
  width: PropTypes.any
};

/**
 * @description Render memoized vector image file media block component
 * @param {Object} props
 * @returns {JSX.Element} Memoized vector image file media block component
 */
export const VectorImageFileMedia = (props) => {
  const { alt, src = '#', className } = props;

  if (!src) return null;

  return <img src={src} alt={alt} className={className} loading="lazy" />;
};

VectorImageFileMedia.propTypes = {
  alt: PropTypes.string,
  src: PropTypes.string
};

/**
 * @description Render memoized manual image file media block component
 * @param {Object} props
 * @returns {JSX.Element} Memoized manual image file media block component
 */
export const ManualImageFileMedia = (props) => {
  const { alt, src = '#', className, srcSet = [], style, disableImageZoom = true } = props;

  if (!src) return null;

  return (
    <span className="picture-wrapper ec-lazy-img ec-lazy-img--loaded " style={srcSet?.length > 0 ? style : {}}>
      <picture
        alt={alt}
        className={classNames('ec-lazy-img ec-lazy-img--loaded', !disableImageZoom && 'image--zoomable')}
        style={style}
      >
        {srcSet?.map((item, index) => {
          const itemSrcSet = (item.srcSet || []).map((s) => src + '?' + s).join(', ');
          return <source key={index} media={item.media} srcSet={itemSrcSet} />;
        })}

        <img src={src} alt={alt} className={className} loading="lazy" />
      </picture>
    </span>
  );
};

ManualImageFileMedia.propTypes = {
  alt: PropTypes.string,
  height: PropTypes.any,
  src: PropTypes.string,
  width: PropTypes.any,
  pixelRatio: PropTypes.any
};

/**
 * @description Render memoized normal image file media block component
 * @param {Object} props
 * @returns {JSX.Element} Memoized normal image file media block component
 */
export const NormalImageUrlMedia = (props) => {
  const { alt, src = '#', srcSet = [], style, className, disableImageZoom = true } = props;

  return (
    <span className="picture-wrapper ec-lazy-img img-padding ec-lazy-img--instant ec-lazy-img--loaded" style={style}>
      <picture
        alt={alt}
        className={classNames(
          'ec-lazy-img img-padding ec-lazy-img--instant ec-lazy-img--loaded',
          !disableImageZoom && 'image--zoomable'
        )}
        style={style}
      >
        {srcSet?.map((item, index) => (
          <source
            key={item.media || index}
            data-srcset={[src, item.mode && `?width=${item.width}&height=${item.height}&mode=${item.mode}`]
              .filter(Boolean)
              .join('')}
            srcSet={[src, item.mode && `?width=${item.width}&height=${item.height}&mode=${item.mode}`]
              .filter(Boolean)
              .join('')}
            media={item.media}
          />
        ))}

        <img src={src} alt={alt} className={className} loading="lazy" />
      </picture>
    </span>
  );
};

NormalImageUrlMedia.propTypes = {
  alt: PropTypes.string,
  src: PropTypes.string,
  srcSet: PropTypes.array
};
